import {
  LabelRegular,
  Pill,
  Table,
  Tooltip,
  useSortableTable,
  type TableColumn
} from '@hm/fundamental'

import type {ParseError} from '../../helpers/excelParser'
import {useRiskSurveyTranslation} from '../../hooks'
import type {CreateRiaRequest} from '../../types'

type ImportPreviewTableProps = {
  rows: CreateRiaRequest[]
  errors: ParseError[]
  excludedRows: Set<number>
  onToggleRow: (index: number) => void
}

type RowData = CreateRiaRequest & {_index: number}

export const ImportPreviewTable = ({
  rows,
  errors,
  excludedRows,
  onToggleRow
}: ImportPreviewTableProps) => {
  const {t} = useRiskSurveyTranslation()

  const validCount = rows.length - excludedRows.size
  const totalCount = rows.length

  const columns: TableColumn<RowData>[] = [
    {
      id: '_index' as keyof RowData,
      label: '',
      render: (row) => {
        const isIncluded = !excludedRows.has(row._index)
        return (
          <input
            key={`${row._index}-${isIncluded}`}
            type="checkbox"
            checked={isIncluded}
            onChange={() => onToggleRow(row._index)}
            className="h-4 w-4 cursor-pointer accent-primary"
            data-cy={`import-row-checkbox-${row._index}`}
          />
        )
      }
    },
    {id: 'riNumber', label: t('table.riNumber')},
    {id: 'title', label: t('table.title')},
    {id: 'priority', label: t('table.priority')},
    {id: 'costClassification', label: t('table.costClassification')},
    {
      id: 'lePrior',
      label: t('table.lePrior'),
      render: (row) =>
        row.lePrior !== null && row.lePrior !== undefined ? String(row.lePrior) : '—'
    },
    {
      id: 'leAfter',
      label: t('table.leAfter'),
      render: (row) =>
        row.leAfter !== null && row.leAfter !== undefined ? String(row.leAfter) : '—'
    },
    {
      id: 'indicativeTimeScale',
      label: t('table.indicativeTimeScale'),
      render: (row) => row.indicativeTimeScale ?? '—'
    }
  ]

  const data = rows.map((row, index) => ({...row, _index: index}))

  const config = useSortableTable(columns, [excludedRows], {
    prefix: 'import-row',
    postfixFieldName: 'riNumber'
  })

  return (
    <div>
      <LabelRegular className="mb-2">
        {t('import.validSummary', {valid: validCount, total: totalCount})}
      </LabelRegular>

      {errors.length > 0 && (
        <div className="mb-2">
          {errors.map((err, idx) => {
            const translatedMessage = t(err.message, err.messageParams ?? {})
            const translatedField = err.field ? t(`table.${err.field}`) : ''
            return (
              <Tooltip
                key={idx}
                content={
                  err.field
                    ? t('validation.rowFieldError', {
                        row: err.row,
                        field: translatedField,
                        message: translatedMessage
                      })
                    : t('validation.rowError', {row: err.row, message: translatedMessage})
                }
              >
                <span className="inline-block mr-1 mb-1">
                  <Pill
                    type={err.severity === 'warning' ? 'warning' : 'negative'}
                    text={t('validation.rowError', {row: err.row, message: translatedMessage})}
                    size="regular"
                  />
                </span>
              </Tooltip>
            )
          })}
        </div>
      )}

      <div className="[&_thead_th]:text-base [&_thead_th]:font-semibold [&_thead_th]:leading-normal [&_thead_th_*]:text-base [&_thead_th_*]:font-semibold [&_thead_th_*]:leading-normal">
        <Table
          config={config}
          data={data}
          getRowId={(row) => String(row._index)}
          breakpoint="sm"
          data-cy="import-preview-table"
        />
      </div>
    </div>
  )
}
